// 我的帳號 › 通知（NOTIF-02，VMS UX 評審 2026-10-13 P1-10）分三段：
//   ① 浮動通知與彈窗（這台電腦）：浮動通知三檔（UX-14）、警報彈窗（LIVE-09），存瀏覽器
//   ② 我要收哪些通知：全域（勿擾時段、事件通知 email、鎖定畫面隱藏細節、縮圖）與家戶層（類別 × 管道；設備覆寫偵測通知）
//   ③ 訂閱的場域與群組（〔＋ 訂閱場域〕）
// 每一列各自儲存。不可關閉的類別（notify forced list）顯示但不能關。偏好存在 notify，App 與 VMS 共用同一份。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageAnchors } from '../../components/PageAnchors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useHomeLabel, useScope } from '../../lib/scope';
import { useDevices } from '../../lib/useDevices';
import { fullName } from '../../lib/devices';
import { PopupPrefsCard } from './PopupPrefsCard';
import { ToastPrefs } from './ToastPrefs';
import { NodeSubscriptions } from '../orgtree/NodeSubscriptions';
import { effective, notifySettingsApi, validHHMM, type NotifySettings } from '../../lib/notifySettings';

function useRowSave() {
  const [state, setState] = useState<{ key: string; s: 'saving' | 'saved' | 'error'; msg?: string } | null>(null);
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setState({ key, s: 'saving' });
    try {
      await fn();
      setState({ key, s: 'saved' });
    } catch (e) {
      setState({ key, s: 'error', msg: String((e as { code?: string; message?: string }).code ?? (e as Error).message ?? e) });
    }
  };
  return { state, run };
}

function RowState({ k, st }: { k: string; st: ReturnType<typeof useRowSave>['state'] }) {
  const { t } = useTranslation();
  if (st?.key !== k) return null;
  if (st.s === 'saving') return <span className="small muted">{t('common.saving')}</span>;
  if (st.s === 'saved') return <span className="small muted">{t('policy.saved')}</span>;
  return <span className="small error">{st.msg}</span>;
}

export function NotifySection() {
  const { t } = useTranslation();
  const { visible } = useScope();
  const homeLabel = useHomeLabel();
  const memberHomes = visible.filter((h) => !!h.role);
  const [homeId, setHomeId] = useState('');
  const home = memberHomes.find((h) => h.home_id === homeId) ?? memberHomes[0];
  const local = (
    <div className="nset-part anchor-target" id="nset-local" data-testid="nset-local">
      <h2 className="nset-part-title">{t('nset.part_local')}</h2>
      {/* 浮動通知與警報卡合成一張卡、一段說明（第三輪 R3-P0-2 ⑤） */}
      <section className="card float-prefs" data-testid="float-prefs">
        <p className="small">{t('nset.float_intro')}</p>
        <ToastPrefs />
        <PopupPrefsCard />
      </section>
    </div>
  );
  const subs = (
    <div className="nset-part anchor-target" id="nset-subs" data-testid="nset-subs">
      <h2 className="nset-part-title">{t('nset.part_subs')}</h2>
      <NodeSubscriptions />
    </div>
  );
  // 段落錨點（第二輪 R2-P2-4：通知頁很長、「訂閱節點」在最底）
  const anchors = (
    <PageAnchors
      items={[
        { id: 'nset-local', label: t('nset.part_local') },
        ...(home ? [{ id: 'nset-receive', label: t('nset.part_receive') }] : []),
        { id: 'nset-subs', label: t('nset.part_subs') },
      ]}
    />
  );
  if (!home)
    return (
      <>
        {anchors}
        {local}
        <div className="nset-part">
          <h2 className="nset-part-title">{t('nset.part_receive')}</h2>
          <p className="card muted">{t('nset.no_home')}</p>
        </div>
        {subs}
      </>
    );
  return (
    <>
      {anchors}
      {local}
      <div className="nset-part anchor-target" id="nset-receive" data-testid="nset-receive">
      <h2 className="nset-part-title">{t('nset.part_receive')}</h2>
      <GlobalCard api={home.endpoints.api} />
      <section className="card" aria-label={t('nset.home_title')}>
        <h2 className="section-title">{t('nset.home_title')}</h2>
        {memberHomes.length > 1 && (
          <select className="input" style={{ width: 'auto' }} value={home.home_id} onChange={(e) => setHomeId(e.target.value)} aria-label={t('nset.home')}>
            {memberHomes.map((h) => (
              <option key={h.home_id} value={h.home_id}>
                {homeLabel(h)}
              </option>
            ))}
          </select>
        )}
        <HomeCategories api={home.endpoints.api} homeId={home.home_id} />
      </section>
      </div>
      {subs}
    </>
  );
}

function GlobalCard({ api }: { api: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['nset-mine', api], queryFn: () => notifySettingsApi.mine(api), retry: false });
  const { state, run } = useRowSave();
  const [start, setStart] = useState<string | null>(null);
  const [end, setEnd] = useState<string | null>(null);
  const s = q.data;
  const put = (key: string, body: Partial<NotifySettings>) =>
    run(key, async () => {
      await notifySettingsApi.putMine(api, body);
      await qc.invalidateQueries({ queryKey: ['nset-mine', api] });
    });
  const qs = start ?? s?.quiet_hours?.start ?? '';
  const qe = end ?? s?.quiet_hours?.end ?? '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <section className="card" aria-label={t('nset.global_title')}>
      <h2 className="section-title">{t('nset.global_title')}</h2>
      {q.isError && <p className="muted">{t('nset.unavailable')}</p>}
      {s && (
        <ul className="plain-list">
          <li className="policy-row" data-nset="quiet">
            <span>{t('nset.quiet')}</span>
            <input
              className="input"
              style={{ width: 90 }}
              aria-label={t('nset.quiet_start')}
              placeholder="22:00"
              value={qs}
              onChange={(e) => setStart(e.target.value)}
            />
            –
            <input
              className="input"
              style={{ width: 90 }}
              aria-label={t('nset.quiet_end')}
              placeholder="07:00"
              value={qe}
              onChange={(e) => setEnd(e.target.value)}
            />
            <button
              className="btn btn-sm"
              disabled={!(qs === '' && qe === '') && !(validHHMM(qs) && validHHMM(qe))}
              onClick={() => void put('quiet', { quiet_hours: qs ? { start: qs, end: qe, tz } : { start: '', end: '' } })}
            >
              {qs ? t('common.save') : t('nset.quiet_clear')}
            </button>
            <span className="small muted">{t('nset.quiet_hint', { tz })}</span>
            <RowState k="quiet" st={state} />
          </li>
          {(['ai_confirmed_only', 'email_alerts', 'hide_details', 'thumbnails'] as const).map((k) => (
            <li key={k} className="policy-row" data-nset={k}>
              <label className="check">
                <input type="checkbox" checked={!!s[k]} onChange={(e) => void put(k, { [k]: e.target.checked })} />
                {t(`nset.${k}`)}
              </label>
              <RowState k={k} st={state} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function HomeCategories({ api, homeId }: { api: string; homeId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { cameras, tree } = useDevices();
  const q = useQuery({ queryKey: ['nset-home', homeId], queryFn: () => notifySettingsApi.home(api, homeId), retry: false });
  const { state, run } = useRowSave();
  const s = q.data;
  if (q.isError) return <p className="muted">{t('nset.unavailable')}</p>;
  if (!s) return <p className="muted">{t('common.loading')}</p>;
  const put = (key: string, body: Partial<NotifySettings>) =>
    run(key, async () => {
      await notifySettingsApi.putHome(api, homeId, body);
      await qc.invalidateQueries({ queryKey: ['nset-home', homeId] });
    });
  const cats = [...s.forced, ...s.configurable];
  const homeCams = cameras.filter((c) => c.homeId === homeId);
  return (
    <>
      <table className="grid" data-testid="nset-categories">
        <thead>
          <tr>
            <th>{t('nset.category')}</th>
            <th>{t('nset.ch_inbox')}</th>
            <th>{t('nset.ch_push')}</th>
            <th>{t('nset.ch_email')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {cats.map((c) => {
            const forced = s.forced.includes(c);
            const e = effective(s.categories?.[c]);
            const set = (field: 'enabled' | 'push' | 'email', v: boolean) => void put(c, { categories: { [c]: { ...s.categories?.[c], [field]: v } } });
            return (
              <tr key={c} data-category={c}>
                <td>
                  {t(`nset.cat.${c.replace(/\./g, '_')}`, { defaultValue: t('nset.cat_other') })}
                  {forced && (
                    <span className="badge" style={{ marginLeft: 6 }}>
                      {t('nset.forced')}
                    </span>
                  )}
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={t('nset.ch_inbox')}
                    checked={e.enabled}
                    disabled={forced}
                    onChange={(ev) => set('enabled', ev.target.checked)}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={t('nset.ch_push')}
                    checked={e.push}
                    disabled={forced || !e.enabled}
                    onChange={(ev) => set('push', ev.target.checked)}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={t('nset.ch_email')}
                    checked={e.email}
                    disabled={!e.enabled}
                    onChange={(ev) => set('email', ev.target.checked)}
                  />
                </td>
                <td>
                  <RowState k={c} st={state} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {homeCams.length > 0 && (
        <>
          <h3 className="section-title small">{t('nset.per_device')}</h3>
          <ul className="plain-list" data-testid="nset-devices">
            {homeCams.map((d) => {
              const p = s.things?.[d.aid]?.['device.detection'];
              const on = p?.enabled ?? effective(s.categories?.['device.detection']).enabled;
              const k = 'dev:' + d.aid;
              return (
                <li key={d.key} className="policy-row" data-device={d.aid}>
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={(ev) => void put(k, { things: { [d.aid]: { 'device.detection': { enabled: ev.target.checked } } } })}
                    />
                    {fullName(d, tree)}
                  </label>
                  {p && (
                    <button className="btn btn-sm" onClick={() => void put(k, { things: { [d.aid]: { 'device.detection': { reset: true } } } })}>
                      {t('nset.follow_home')}
                    </button>
                  )}
                  <RowState k={k} st={state} />
                </li>
              );
            })}
          </ul>
        </>
      )}
    </>
  );
}
